import type {
    IGetConsumptionDashboardRequest,
    IGetTenantConsumptionAnalyticsRequest,
    IGetTenantConsumptionDashboardSummaryRequest,
    IGetTenantFolderConsumptionBreakdownRequest,
    IGetTenantProcessConsumptionBreakdownRequest,
    IOrganizationConsumptionDashboard,
    ITenantConsumptionAnalytics,
    ITenantConsumptionDashboardSummary,
    ITenantFolderConsumptionBreakdown,
    ITenantProcessConsumptionBreakdown,
} from '@experiences/interfaces';

import { post } from '../utility/Requests';

const CONSUMPTION_DASHBOARD_API = '/api/consumption-dashboard';

/**
 * Get organization consumption dashboard data
 * Fetches aggregated data combining tenant allocations and consumption
 *
 * @param productCode - The product code to get dashboard data for
 * @param includeTopUpBreakdown - When true, request the user top-up (AgenticPool.TopUpGrant) breakdown
 * @returns Promise with organization consumption dashboard data
 */
export async function getOrganizationConsumptionDashboard(
    productCode: string,
    includeTopUpBreakdown = false,
): Promise<IOrganizationConsumptionDashboard> {
    const payload: IGetConsumptionDashboardRequest = {
        productCode,
        ...(includeTopUpBreakdown ? { includeTopUpBreakdown: true } : {}),
    };
    return post<IOrganizationConsumptionDashboard>(
        `${CONSUMPTION_DASHBOARD_API}/organization-dashboard`,
        { body: payload },
    );
}

/**
 * Get tenant consumption dashboard summary
 * Fetches tenant summary data required by tenant-level dashboard header cards.
 *
 * @param productCode - The product code to get dashboard data for
 * @param tenantId - The tenant id to get dashboard data for
 * @param serviceType - The LRM service type for this product (e.g., 'tenant', 'orchestrator')
 * @param startDate - Optional custom range start date (Unix timestamp in seconds)
 * @param endDate - Optional custom range end date (Unix timestamp in seconds)
 * @returns Promise with tenant consumption dashboard summary data
 */
export async function getTenantConsumptionDashboardSummary(
    productCode: string,
    tenantId: string,
    serviceType: string,
    startDate?: number,
    endDate?: number,
): Promise<ITenantConsumptionDashboardSummary> {
    const payload: IGetTenantConsumptionDashboardSummaryRequest & { serviceType: string } = {
        productCode,
        tenantId,
        serviceType,
        ...(startDate != null && endDate != null ? {
            startDate,
            endDate,
        } : {}),
    };
    return post<ITenantConsumptionDashboardSummary>(
        `${CONSUMPTION_DASHBOARD_API}/tenant-dashboard-summary`,
        { body: payload },
    );
}

export async function getTenantConsumptionDashboardSummaryByTenant(
    productCode: string,
    tenantId: string,
    serviceType: string,
    startDate?: number,
    endDate?: number,
): Promise<ITenantConsumptionDashboardSummary> {
    const payload: IGetTenantConsumptionDashboardSummaryRequest & { serviceType: string } = {
        productCode,
        tenantId,
        serviceType,
        ...(startDate != null && endDate != null ? {
            startDate,
            endDate,
        } : {}),
    };
    return post<ITenantConsumptionDashboardSummary>(
        `${CONSUMPTION_DASHBOARD_API}/tenant-dashboard-summary-by-tenant`,
        { body: payload },
    );
}

export interface IOrganizationTenantConsumptionResponse {
    consumedFromOrgWithoutTenant: number;
    tenantConsumptionItems: Array<{
        tenantId: string;
        consumedFromTenantPool: number;
        consumedFromOrgPool: number;
    }>;
    userTopUpConsumed?: number;
}

/**
 * Get organization tenant consumption data filtered by a custom date range.
 * Returns raw LA consumption per tenant (no allocation/health data).
 *
 * @param productCode - The product code
 * @param startDate - Interval start, unix timestamp in seconds
 * @param endDate - Interval end, unix timestamp in seconds
 * @param includeTopUpBreakdown - When true, request the user top-up (AgenticPool.TopUpGrant) breakdown
 * @returns Promise with per-tenant consumption data from LA
 */
export async function getOrganizationFilteredTenantData(
    productCode: string,
    startDate: number,
    endDate: number,
    includeTopUpBreakdown = false,
): Promise<IOrganizationTenantConsumptionResponse> {
    return post<IOrganizationTenantConsumptionResponse>(
        `${CONSUMPTION_DASHBOARD_API}/organization-tenant-consumption`,
        {
            body: {
                productCode,
                startDate,
                endDate,
                ...(includeTopUpBreakdown ? { includeTopUpBreakdown: true } : {}),
            },
        },
    );
}

/**
 * Get tenant consumption analytics data for distribution and services cards
 *
 * @param productCode - The product code to get analytics for
 * @param tenantId - The tenant id to get analytics for
 * @param startDate - Interval start, unix timestamp in seconds
 * @param endDate - Interval end, unix timestamp in seconds
 * @param folderKeys - Optional list of folder keys to scope analytics to specific folders (folder view)
 * @param aggregateByOwnership - Optional flag to aggregate by ownership (true) or execution (false), used in folder view
 * @returns Promise with tenant analytics data
 */
export async function getTenantConsumptionAnalytics(
    productCode: string,
    tenantId: string,
    startDate: number,
    endDate: number,
    folderKeys?: string[],
    aggregateByOwnership?: boolean,
    timeZoneId?: string,
): Promise<ITenantConsumptionAnalytics> {
    const payload: IGetTenantConsumptionAnalyticsRequest = {
        productCode,
        tenantId,
        startDate,
        endDate,
        ...(folderKeys?.length ? { folderKeys } : {}),
        ...(typeof aggregateByOwnership === 'boolean' ? { aggregateByOwnership } : {}),
        ...(timeZoneId ? { timeZoneId } : {}),
    };
    return post<ITenantConsumptionAnalytics>(
        `${CONSUMPTION_DASHBOARD_API}/tenant-consumption-breakdown`,
        { body: payload },
    );
}

/**
 * Consumables V2 per-unit "Overview" charts: daily consumption by service for one consumable,
 * org-wide when `tenantId` is omitted, tenant-scoped otherwise. Dates are unix seconds.
 * Same response shape as the tenant analytics above.
 */
export async function getOrgDailyConsumptionByService(
    productCode: string,
    startDate: number,
    endDate: number,
): Promise<ITenantConsumptionAnalytics> {
    return post<ITenantConsumptionAnalytics>(
        `${CONSUMPTION_DASHBOARD_API}/org-daily-consumption-by-service`,
        {
            body: {
                productCode,
                startDate,
                endDate,
            },
        },
    );
}

export async function getTenantDailyConsumptionByService(
    productCode: string,
    tenantId: string,
    startDate: number,
    endDate: number,
): Promise<ITenantConsumptionAnalytics> {
    return post<ITenantConsumptionAnalytics>(
        `${CONSUMPTION_DASHBOARD_API}/tenant-daily-consumption-by-service`,
        {
            body: {
                productCode,
                tenantId,
                startDate,
                endDate,
            },
        },
    );
}

/**
 * Get tenant folder consumption breakdown
 * Fetches per-folder consumption data, aggregated by ownership or execution.
 *
 * @param productCode - The product code to get breakdown data for
 * @param tenantId - The tenant id to get breakdown data for
 * @param startDate - Interval start, unix timestamp in seconds
 * @param endDate - Interval end, unix timestamp in seconds
 * @param aggregateByOwnership - If true, aggregates by folder ownership; otherwise by execution
 * @param alwaysIncludeFolderKeys - Folder keys returned even with zero consumption in the range
 * @returns Promise with tenant folder consumption breakdown data
 */
export async function getTenantFolderConsumptionBreakdown(
    productCode: string,
    tenantId: string,
    startDate: number,
    endDate: number,
    aggregateByOwnership: boolean,
    alwaysIncludeFolderKeys?: string[],
): Promise<ITenantFolderConsumptionBreakdown> {
    const payload: IGetTenantFolderConsumptionBreakdownRequest = {
        productCode,
        tenantId,
        startDate,
        endDate,
        aggregateByOwnership,
        ...(alwaysIncludeFolderKeys && alwaysIncludeFolderKeys.length > 0 ? { alwaysIncludeFolderKeys } : {}),
    };

    return post<ITenantFolderConsumptionBreakdown>(
        `${CONSUMPTION_DASHBOARD_API}/tenant-folder-consumption-breakdown`,
        { body: payload },
    );
}

/**
 * Get tenant process consumption breakdown
 * Fetches per-process consumption data, supporting filtering by folder, process type, and search term.
 *
 * @param payload - Request parameters including productCode, tenantId, date range, filters, and pagination
 * @returns Promise with tenant process consumption breakdown data
 */
export async function getTenantProcessConsumptionBreakdown(
    payload: IGetTenantProcessConsumptionBreakdownRequest,
): Promise<ITenantProcessConsumptionBreakdown> {
    return post<ITenantProcessConsumptionBreakdown>(
        `${CONSUMPTION_DASHBOARD_API}/tenant-process-consumption-breakdown`,
        { body: payload },
    );
}
